import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { draftKudos, sendKudos } from '../api.js';
import {
  MESSAGE_MAX,
  MESSAGE_MIN,
  RECIPIENT_EMAIL_MAX,
  RECIPIENT_NAME_MAX,
  SENDER_EMAIL_MAX,
  TRAIT_MAX,
} from '../constants.js';
import ModerationResult from './ModerationResult.jsx';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Rotating examples so the textarea never feels blank-page-cold.
// All taken from the kinds of things people actually say.
const MESSAGE_EXAMPLES = [
  "You\u2019re the only person at work who asks how I\u2019m actually doing.",
  "The way you handled that meeting last week was masterful.",
  "Your laugh is contagious \u2014 the room is better when you\u2019re in it.",
  "You make hard things look easy, and you do it without bragging.",
  "I notice you remember everyone\u2019s name. That\u2019s a rare kindness.",
];

export default function ComposeForm({ parentKudosId }) {
  const navigate = useNavigate();
  const turnstileRef = useRef(null);

  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  // Draft assist (optional helper, collapsed by default)
  const [showHelper, setShowHelper] = useState(false);
  const [trait, setTrait] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState('');

  // Rotating placeholder index (only advances while message is empty)
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  useEffect(() => {
    if (message) return;
    const id = setInterval(
      () => setPlaceholderIdx((i) => (i + 1) % MESSAGE_EXAMPLES.length),
      4500
    );
    return () => clearInterval(id);
  }, [message]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [moderation, setModeration] = useState(null);

  const valid = useMemo(() => {
    const trimmedName = recipientName.trim();
    const trimmedEmail = recipientEmail.trim();
    const trimmedMessage = message.trim();
    const trimmedSender = senderEmail.trim();
    return (
      trimmedName.length > 0 &&
      trimmedName.length <= RECIPIENT_NAME_MAX &&
      EMAIL_RE.test(trimmedEmail) &&
      trimmedEmail.length <= RECIPIENT_EMAIL_MAX &&
      trimmedMessage.length >= MESSAGE_MIN &&
      trimmedMessage.length <= MESSAGE_MAX &&
      EMAIL_RE.test(trimmedSender) &&
      trimmedSender.length <= SENDER_EMAIL_MAX &&
      turnstileToken.length > 0
    );
  }, [recipientName, recipientEmail, message, senderEmail, turnstileToken]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!valid || submitting) return;
    setError(null);
    setModeration(null);
    setSubmitting(true);

    try {
      const result = await sendKudos({
        recipientName: recipientName.trim(),
        recipientEmail: recipientEmail.trim(),
        message: message.trim(),
        senderEmail: senderEmail.trim(),
        turnstileToken,
        parentKudosId,
      });

      if (result?.moderation && !result.moderation.kind) {
        setModeration(result.moderation);
        return;
      }

      navigate('/sent', {
        state: {
          recipientName: recipientName.trim(),
          message: message.trim(),
        },
      });
    } catch (err) {
      if (err.data?.moderation) {
        setModeration(err.data.moderation);
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
      turnstileRef.current?.reset?.();
      setTurnstileToken('');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDraft() {
    const name = recipientName.trim();
    const traitVal = trait.trim();
    if (!name || traitVal.length < 3) {
      setDraftError('Add their first name and one true thing about them first.');
      return;
    }
    setDrafting(true);
    setDraftError('');
    setDrafts([]);
    try {
      const data = await draftKudos({ recipientFirstName: name, trait: traitVal });
      setDrafts(Array.isArray(data?.drafts) ? data.drafts : []);
    } catch (err) {
      setDraftError(err.message || 'Couldn’t draft anything just now.');
    } finally {
      setDrafting(false);
    }
  }

  function pickDraft(d) {
    setMessage(d);
    setDrafts([]);
  }

  const trimmedMessage = message.trim();
  const showPreview = trimmedMessage.length >= MESSAGE_MIN;

  return (
    <form onSubmit={onSubmit} className="card max-w-xl mx-auto space-y-4">
      <div>
        <label htmlFor="recipientName" className="label">
          Who&rsquo;s it for?
        </label>
        <input
          id="recipientName"
          className="input"
          autoComplete="off"
          maxLength={RECIPIENT_NAME_MAX}
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          placeholder="e.g. Alex"
          required
        />
      </div>

      <div>
        <label htmlFor="recipientEmail" className="label">
          Where should we send it?
        </label>
        <input
          id="recipientEmail"
          type="email"
          className="input"
          autoComplete="off"
          maxLength={RECIPIENT_EMAIL_MAX}
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          placeholder="alex@example.com"
          required
        />
        <p className="text-xs text-navy-500 mt-1">
          We use this once to deliver the message, then forget it.
        </p>
      </div>

      <div>
        <label htmlFor="message" className="label">
          What do you want them to know?
        </label>
        <textarea
          id="message"
          className="textarea-hero min-h-[180px]"
          maxLength={MESSAGE_MAX}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={`e.g. ${MESSAGE_EXAMPLES[placeholderIdx]}`}
          required
        />
        <div className="flex justify-between items-center text-xs text-navy-500 mt-1">
          <span>
            {trimmedMessage.length < MESSAGE_MIN
              ? `${MESSAGE_MIN - trimmedMessage.length} more characters`
              : 'Looks good.'}
          </span>
          <span>
            {message.length} / {MESSAGE_MAX}
          </span>
        </div>
        <div className="mt-2">
          <button
            type="button"
            className="text-sm text-teal-700 hover:underline"
            onClick={() => setShowHelper((v) => !v)}
            aria-expanded={showHelper}
          >
            {showHelper ? 'Hide writing helper' : '✨ Stuck? Help me write it'}
          </button>
        </div>
      </div>

      {showHelper && (
        <div className="rounded-2xl border border-teal-500/30 bg-teal-50/60 p-4 space-y-3">
          <p className="text-xs text-navy-700">
            Tell us one true thing about {recipientName.trim() || 'them'} and we&rsquo;ll suggest three drafts.
          </p>
          <input
            id="trait"
            className="input"
            maxLength={TRAIT_MAX}
            placeholder="e.g. always shows up early, made me laugh when I needed it"
            value={trait}
            onChange={(e) => setTrait(e.target.value)}
          />
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="text-sm font-medium text-teal-700 hover:underline disabled:opacity-50"
              onClick={handleDraft}
              disabled={drafting}
            >
              {drafting ? 'Drafting…' : 'Draft three options'}
            </button>
            {draftError && (
              <span className="text-xs text-orange-600">{draftError}</span>
            )}
          </div>
          {drafts.length > 0 && (
            <div className="space-y-2 pt-1">
              <p className="text-xs text-navy-500">Pick one to use it &mdash; you can still edit.</p>
              {drafts.map((d, di) => (
                <button
                  type="button"
                  key={di}
                  className="block w-full text-left rounded-xl border border-teal-500/30 bg-white px-4 py-3 text-sm text-navy hover:bg-teal-50"
                  onClick={() => pickDraft(d)}
                >
                  {d}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {showPreview && (
        <div>
          <p className="label">Preview</p>
          <div className="rounded-2xl border border-navy/10 bg-cream/70 px-5 py-4">
            <p className="text-xs text-navy-500 mb-2">
              To:{' '}
              <strong className="text-navy">
                {recipientName.trim() || 'Their name'}
              </strong>
              <span className="mx-2">&middot;</span>
              From: <em>someone who appreciates you</em>
            </p>
            <div className="rounded-xl bg-white/80 px-4 py-3 text-navy whitespace-pre-wrap text-[15px] leading-relaxed">
              {trimmedMessage}
            </div>
            <p className="text-xs text-navy-500 mt-3">
              Roughly what they&rsquo;ll see in their inbox. Your name is never sent.
            </p>
          </div>
        </div>
      )}

      <div>
        <label htmlFor="senderEmail" className="label">
          Your email{' '}
          <span className="text-navy-500 font-normal">(just for us &mdash; never shown to them)</span>
        </label>
        <input
          id="senderEmail"
          type="email"
          className="input"
          autoComplete="email"
          maxLength={SENDER_EMAIL_MAX}
          value={senderEmail}
          onChange={(e) => setSenderEmail(e.target.value)}
          placeholder="you@example.com"
          required
        />
        <p className="text-xs text-navy-500 mt-1">
          Helps us prevent spam. Never shown to your recipient, never used for marketing.
        </p>
      </div>

      {TURNSTILE_SITE_KEY ? (
        <div>
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            onExpire={() => setTurnstileToken('')}
            onError={() => setTurnstileToken('')}
            options={{ theme: 'light', appearance: 'interaction-only' }}
          />
        </div>
      ) : (
        <p className="text-xs text-orange-600">
          Turnstile site key missing &mdash; set <code>VITE_TURNSTILE_SITE_KEY</code>.
        </p>
      )}

      {moderation && <ModerationResult moderation={moderation} />}
      {error && (
        <p className="text-sm text-orange-600" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        className="btn-primary w-full"
        disabled={!valid || submitting}
      >
        {submitting ? 'Sending…' : 'Send the kudos'}
      </button>

      <p className="text-xs text-navy-500 text-center">
        Anonymous to your recipient. AI screens every message for kindness.
      </p>
    </form>
  );
}
